import React from 'react';
import { ErrorBoundaryFallbackProps } from '@backstage/core-app-api';
import { generateErrorReferenceNumber } from '../utils/errorUtils';
import { 
  Button, 
  Typography, 
  Box, 
  Paper, 
  useTheme
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import RefreshIcon from '@material-ui/icons/Refresh';
import HomeIcon from '@material-ui/icons/Home';
import { useServiceRegistryTranslation } from '../i18n/ServiceRegistryLanguageProvider';


export function ErrorBoundaryFallback(props: ErrorBoundaryFallbackProps) {
  const { t } = useServiceRegistryTranslation();
  const { error, resetError } = props;
  const theme = useTheme();
  const errorReferenceNumber = generateErrorReferenceNumber();

  // Extract component name if available
  const errorMessage = error?.message || String(error);
  let componentName = '';
  
  if (error instanceof Error && error.stack) {
    try {
      const stackLines = error.stack.split('\n');
      for (const line of stackLines) {
        const match = line.match(/at\s+([A-Za-z0-9_]+)\s+\(/);
        if (match && match[1] && !match[1].startsWith('_')) {
          componentName = match[1];
          break;
        }
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }

  
  const errorLocation = componentName 
    ? t('errors.location', { component: componentName })
    : '';

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      width="100%"
      style={{ 
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(4),
      }}
    >
      <Paper 
        elevation={3} 
        style={{
          width: '100%', 
          maxWidth: '650px',
          padding: theme.spacing(5),
          textAlign: 'center',
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box mb={2}>
          <ErrorIcon style={{ 
            fontSize: 70, 
            color: theme.palette.error.main 
          }} />
        </Box>
        
        <Typography 
          variant="h4" 
          gutterBottom
          style={{
            marginBottom: theme.spacing(3),
            fontWeight: 600,
            color: theme.palette.error.main,
          }}
        >
         {t('errors.title')}
        </Typography>
        
        <Typography 
          variant="body1" 
          paragraph
          style={{
            marginBottom: theme.spacing(4),
            fontSize: '1.1rem',
            color: theme.palette.text.primary,
          }}
        >
            {t('errors.message')}{' '}
            <Typography 
              component="a"
              href={`${window.location.origin}/ep-devp/support`}
              style={{
                color: '#4A90E2', // Same blue color as in the image
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}
            >
              {t('errors.contactSupport')}
            </Typography>.
          </Typography>
        
        <Box 
          mb={4}
          p={3}
          style={{
            backgroundColor: theme.palette.type === 'dark' 
              ? theme.palette.grey[900] 
              : theme.palette.grey[100],
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography 
            variant="subtitle2" 
            gutterBottom
            style={{
              fontWeight: 600,
              marginBottom: theme.spacing(1),
              color: theme.palette.text.secondary,
            }}
          >
            {errorLocation} {t('errors.reference')}
          </Typography>
          
          <Box 
            component="span"
            p={1}
            px={2}
            style={{
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.type === 'dark' 
                ? theme.palette.grey[800] 
                : theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              display: 'inline-block',
            }}
          >
            #{errorReferenceNumber}
          </Box>
          
          {/* {errorMessage && (
            <Typography 
              variant="body2" 
              style={{
                marginTop: theme.spacing(2),
                color: theme.palette.type === 'dark' 
                  ? theme.palette.error.light 
                  : theme.palette.error.dark,
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                wordBreak: 'break-word',
              }}
            >
              {errorMessage}
            </Typography>
          )} */}
          
          {/* {isDevelopment && error?.stack && (
            <Box 
              mt={2} 
              p={2} 
              bgcolor={theme.palette.type === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'}
              borderRadius={1}
              textAlign="left"
              style={{
                overflowX: 'auto',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              <Typography 
                variant="caption" 
                component="pre"
                style={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                }}
              >
                {error.stack}
              </Typography>
            </Box>
          )} */}
        </Box>
        
        <Box 
          display="flex" 
          justifyContent="center" 
          flexWrap="wrap"
          gap={2}
        >
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
            style={{ padding: theme.spacing(1, 3), margin: theme.spacing(1) }}
            startIcon={<RefreshIcon />}
          >
            {t('errors.refresh')}
          </Button>
          <Button 
            variant="outlined"
            onClick={() => {
              window.location.href = '/';
              resetError();
            }}
            style={{ padding: theme.spacing(1, 3), margin: theme.spacing(1) }}
            startIcon={<HomeIcon />}
          >
            {t('errors.home')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
